import React, { useCallback, useEffect, useState } from 'react'
import { trans } from '../../../../../../../sharedKernel/i18n'
import { ActionButton } from '../style'
import { deleteProvider, postProvider, PostFormat, updateProvider } from '../../../../../../../sharedKernel/api'
import { showToast } from '../../../../../../../components/toast'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { toBase64 } from '../../../../../../../sharedKernel/file/handler'
import { ButtonProps } from 'react-bootstrap'
import { AxiosError, AxiosResponse } from 'axios'
import { showDoubleActionPopUp } from 'components/popup/doubleAction'
import { store } from 'store'
import { IEventForm } from '../constants'
import { extractData } from 'main/pages/hub/company/events/formBuilder/extractData'
import { changeEventFiles } from '../file/store/actions'
import { changeEventConfigSection } from '../store/actions'
import { IHubEventBasicFormState } from '../store/contracts'
import { IEventFileStructure, IHubEventBasicFormFilesState } from '../file/store/contracts'

interface SaveButtonProps extends ButtonProps {
  formStateValues: IHubEventBasicFormState
  formFilesState: IHubEventBasicFormFilesState
  preRedirectFunction?: React.EventHandler<any>
  launchAction?: boolean
  isLaunched?: boolean
  isActive?: boolean
  text?: string
  eventForm: IEventForm[]
  visible?: boolean
  validateForm: any
  setIsNavigationBlocked: React.EventHandler<any>
  setFilesSaved: React.Dispatch<React.SetStateAction<boolean>>
}

export const SaveButton = ({
  formStateValues,
  formFilesState,
  preRedirectFunction,
  setIsNavigationBlocked,
  eventForm,
  validateForm,
  launchAction = false,
  isLaunched = false,
  isActive = false,
  text = 'Salvar',
  visible = false,
  setFilesSaved,
  ...props
}: SaveButtonProps): JSX.Element => {
  useEffect(() => {
    const { startDate, endDate } =
      formStateValues

    if (startDate === null) {
      setIsDisabled(true)
    }
  }, [formStateValues])

  const confirmationMessage = isLaunched
    ? 'Deseja alterar os dados desse evento?'
    : launchAction
      ? 'Deseja publicar esse evento?'
      : 'Deseja salvar esse evento?'

  const [fileName, setFilename] = useState('')
  const [savedEventId, setSavedEventId] = useState('')

  const onSuccessUploadFile = async (): Promise<void> => {
    showToast('success', labels.uploadFileSuccess.replace('{}', fileName))
  }
  const onErrorUploadFile = async (): Promise<void> => {
    showToast('error', labels.uploadFileFail)
  }

  const redirectToVisualizationScreen = (launched: boolean, EventId?: string): void => {
    setIsNavigationBlocked(false)
    setTimeout(() => {
      const existingEventId = EventId ?? savedEventId
      setFilesSaved(launched)
      navigate(`/hub/company/event/${existingEventId}`, {
        state: { setToLaunched: launched }
      })
    }, 2500)
  }

  const onSuccessLaunchEvent = async (): Promise<void> => {
    showToast('success', labels.launchEventSuccess)
    const launched = true
    redirectToVisualizationScreen(launched)
  }

  const onErrorLaunchEvent = async (): Promise<void> => {
    showToast('error', labels.launchEventError)
    const launched = false
    redirectToVisualizationScreen(launched)
  }

  const navigate = useNavigate()
  const { mutate: persistEvent } = postProvider()
  const { mutate: deleteFile } = deleteProvider()
  const { mutate: uploadFile } = postProvider(onSuccessUploadFile, onErrorUploadFile)
  const { mutate: persistLaunchEvent } = updateProvider(onSuccessLaunchEvent, onErrorLaunchEvent)
  const [isDisabled, setIsDisabled] = useState(false)
  const labels = {
    saveEventSuccess: trans('Evento salvo com sucesso'),
    FinalSelectionDateError: trans('Data de Encerramento das Inscrições inválida! Eventos já publicados só podem ter datas posteriores a data atual'),
    EvaluationStartDateError: trans('Data Inicial de Avaliação inválida! Eventos já publicados só podem ter datas posteriores a data atual'),
    uploadFileSuccess: trans('O arquivo {} foi salvo com sucesso'),
    uploadFileFail: trans('Falha no upload'),
    launchEventSuccess: trans('Parabéns, seu evento foi lançado.'),
    launchEventError: trans('Seu evento não foi lançado'),
    invalidForm: trans('Existem dados inválidos no formulário')
  }

  const confirmation = (): any => {
    showDoubleActionPopUp({
      title: text,
      message: confirmationMessage,
      buttons: [{ text: 'Sim' }, { text: 'Não' }],
      action: async () => handleSubmit()
    })
  }

  const postObject: PostFormat = {
    url: 'v1/hub/company/events',
    data: {
      id: formStateValues.id,
      data: formStateValues
    }
  }

  const handleSubmit = useCallback(async (): Promise<void> => {
    extractData(formStateValues, formFilesState, eventForm)
    store.dispatch(changeEventConfigSection(formStateValues))
    store.dispatch(changeEventFiles(formFilesState))
    setIsNavigationBlocked(false)
    setIsDisabled(false)
    persistEvent(postObject, {
      onSuccess: async (response) => handleSavedData(response),
      onError: async (error) => handleSavedDataError(error)
    })
  }, [])

  const handleSavedData = async (response: AxiosResponse): Promise<void> => {
    if (formFilesState.deletedEventFiles.length > 0) {
      await deleteUploadedFiles(formFilesState.deletedEventFiles, response.data.id)
    }
    await uploadFiles(response.data.id, formFilesState.eventFiles)

    setIsDisabled(false)

    setSavedEventId(response.data.id)

    if (launchAction && response.data.data.isLaunched === false) {
      await handleLaunchEvent(response.data.id)
    } else {
      showToast('success', labels.saveEventSuccess)
      const EventId = response.data.id
      const launched = response.data.data.isLaunched
      redirectToVisualizationScreen(launched, EventId)
    }
  }

  const handleSavedDataError = async (error: AxiosError): Promise<void> => {
    setIsDisabled(false)
    const firstError = error.response?.data.errors[0]
    if (firstError.type === 'GreaterThanOrEqualValidator') {
      if (firstError.fieldName === 'ApplicationStartDate' || firstError.fieldName === 'ClosingDate') {
        showToast('error', error.response?.data.errors[0].message)
      }
      if (firstError.fieldName === 'FinalSelectionDate') {
        showToast('error', labels.FinalSelectionDateError)
      }
      if (firstError.fieldName === 'EvaluationStartDate') {
        showToast('error', labels.EvaluationStartDateError)
      }
    }
  }

  const handleLaunchEvent = async (EventId: string): Promise<void> => {
    persistLaunchEvent({ url: `/v1/hub/company/event/${EventId}/launch` })
  }

  const deleteUploadedFiles = async (files: IEventFileStructure[], EventId: string): Promise<void> => {
    files.forEach((file) => {
      if (file.id !== undefined && file.id !== null && !file.id.includes('_idScreen')) {
        deleteFile({ url: `/v1/hub/company/event/${EventId}/file/${file.id ?? ''}` })
      }
    })
  }

  const uploadFiles = useCallback(async (EventId: string, files: IEventFileStructure[] = []): Promise<void> => {
    const processedFiles = await processUploadedFiles(files.filter((file) => file.id == null))

    processedFiles.forEach((file) => {
      setFilename(atob(file.name))
      uploadFile({
        url: `/v1/hub/company/event/${EventId}/file/upload`,
        data: { data: file }
      })
    })
  }, [])

  const processUploadedFiles = async (files: IEventFileStructure[]): Promise<IEventFileStructure[]> => {
    return await Promise.all(
      files.map(async (file): Promise<IEventFileStructure> => {
        let fileContent, mimetype
        if (file.sourceFile !== undefined) {
          fileContent = await toBase64(file.sourceFile)
          mimetype = file.sourceFile.type
        }

        return {
          mimetype: mimetype,
          name: btoa(unescape(encodeURIComponent(file.name))),
          size: file.size,
          type: file.type,
          content: fileContent
        }
      })
    )
  }

  return (
    <ActionButton
      className="btn btn-primary"
      onClick={() => {
        const isFormValid: boolean = validateForm() ?? false
        if (isFormValid) {
          confirmation()
        }
      }}
      {...props}
    >
      {text}
    </ActionButton>
  )
}
